import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["lightIcon", "darkIcon", "button"]
  static values = { dark: Boolean }
  static classes = ["hidden"]

  initialize() {
    this.mediaQuery = window.matchMedia?.("(prefers-color-scheme: dark)") ?? { matches: false, addEventListener: () => {}, removeEventListener: () => {} }
    this.handleSystemChange = this.handleSystemChange.bind(this)
    this.handleTurboLoad = this.handleTurboLoad.bind(this)
  }

  connect() {
    this.darkValue = this._getStoredTheme() ?? this.mediaQuery.matches
    this.mediaQuery.addEventListener("change", this.handleSystemChange)

    // Reapply theme on Turbo navigation to prevent theme loss during redirects
    document.addEventListener("turbo:load", this.handleTurboLoad)
    document.addEventListener("turbo:render", this.handleTurboLoad)
  }

  disconnect() {
    this.mediaQuery.removeEventListener("change", this.handleSystemChange)
    document.removeEventListener("turbo:load", this.handleTurboLoad)
    document.removeEventListener("turbo:render", this.handleTurboLoad)
  }

  handleTurboLoad() {
    // Reapply the stored theme after Turbo navigation
    const storedTheme = this._getStoredTheme()
    if (storedTheme !== null) {
      const isDark = storedTheme
      document.documentElement.classList.toggle("dark", isDark)
      this.darkValue = isDark
    }
  }

  toggle() {
    this.darkValue = !this.darkValue
    this._setStoredTheme(this.darkValue ? "dark" : "light")
  }

  darkValueChanged() {
    document.documentElement.classList.toggle("dark", this.darkValue)
    this._updateIcons()
    this._updateAccessibility()
    this.dispatch("changed", { detail: { dark: this.darkValue } })
  }

  handleSystemChange(event) {
    if (this._getStoredTheme() === null) {
      this.darkValue = event.matches
    }
  }

  _getStoredTheme() {
    try {
      const theme = localStorage.getItem("theme")
      if (theme === "dark") return true
      if (theme === "light") return false
      return null
    } catch {
      return null
    }
  }

  _setStoredTheme(theme) {
    try {
      localStorage.setItem("theme", theme)
    } catch {
      // localStorage unavailable (Safari private mode)
    }
  }

  _updateIcons() {
    if (!this.hasLightIconTarget || !this.hasDarkIconTarget) return
    this.lightIconTarget.classList.toggle(this.hiddenClass, this.darkValue)
    this.darkIconTarget.classList.toggle(this.hiddenClass, !this.darkValue)
  }

  _updateAccessibility() {
    if (!this.hasButtonTarget) return
    this.buttonTarget.setAttribute("aria-pressed", this.darkValue)
  }
}
